"use client";

import { useEffect, useRef, useState } from "react";

export type ApplicationAddressValue = {
  business_address_line_1: string;
  business_address_line_2: string;
  business_address_city: string;
  business_address_state: string;
  business_address_postal_code: string;
  business_address_country_code: string;
  business_address_source: "google_places" | "manual";
  business_address_place_id: string;
  business_address_verification_token: string;
};

type Suggestion = { placeId: string; text: string };

export default function ApplicationAddressField({
  disabled,
  onChange,
  value,
}: {
  disabled: boolean;
  onChange: (value: ApplicationAddressValue) => void;
  value: ApplicationAddressValue;
}) {
  const sessionToken = useRef(crypto.randomUUID());
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [notice, setNotice] = useState("Start typing and choose an address, or enter it manually below.");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.trim().length < 3 || value.business_address_source === "google_places") return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch("/api/applications/address/suggestions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ input: query, sessionToken: sessionToken.current }),
          signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok || result.configured === false) {
          setSuggestions([]);
          setNotice("Address suggestions are not available. Enter the structured address manually.");
          return;
        }
        setSuggestions(Array.isArray(result.suggestions) ? result.suggestions : []);
        setNotice(result.suggestions?.length ? "Choose the matching address." : "No match found. Enter the address manually.");
      } catch (error) {
        if ((error as { name?: string }).name !== "AbortError") {
          setSuggestions([]);
          setNotice("Address suggestions are temporarily unavailable. Enter the address manually.");
        }
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [query, value.business_address_source]);

  function manual(field: keyof ApplicationAddressValue, fieldValue: string) {
    onChange({
      ...value,
      [field]: fieldValue,
      business_address_source: "manual",
      business_address_place_id: "",
      business_address_verification_token: "",
    });
  }

  async function choose(suggestion: Suggestion) {
    setLoading(true);
    try {
      const response = await fetch("/api/applications/address/resolve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ placeId: suggestion.placeId, sessionToken: sessionToken.current }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Address lookup failed.");
      onChange({ ...result.address, business_address_verification_token: result.verificationToken });
      setQuery(suggestion.text);
      setSuggestions([]);
      setNotice("Address selected. Review every field; editing switches to manual entry.");
      sessionToken.current = crypto.randomUUID();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Address lookup failed. Enter it manually.");
    } finally {
      setLoading(false);
    }
  }

  const inputClass = "min-h-12 rounded-md border border-[var(--vintera-border)] bg-white px-3 py-2 text-base font-normal text-[var(--vintera-ink)] outline-none focus:border-[var(--vintera-navy)] focus:ring-2 focus:ring-[#092a5a]/15";
  return (
    <fieldset className="md:col-span-2 rounded-md border border-[var(--vintera-border)] bg-[var(--vintera-sage-soft)] p-4">
      <legend className="px-1 text-sm font-bold text-[var(--vintera-navy)]">Business address *</legend>
      <label className="mt-2 block text-sm font-semibold text-slate-800">
        Find address
        <input disabled={disabled} value={query} onChange={(event) => { setQuery(event.target.value); setSuggestions([]); if (value.business_address_source === "google_places") manual("business_address_line_1", value.business_address_line_1); }} autoComplete="off" placeholder="Start with street number and name" className={`mt-2 w-full ${inputClass}`} />
      </label>
      {suggestions.length > 0 && <div role="listbox" aria-label="Address suggestions" className="mt-2 overflow-hidden rounded-md border border-[var(--vintera-border)] bg-white shadow-sm">{suggestions.map((suggestion) => <button key={suggestion.placeId} type="button" role="option" aria-selected="false" onClick={() => void choose(suggestion)} className="block min-h-11 w-full border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-[var(--vintera-sage-soft)]">{suggestion.text}</button>)}</div>}
      <div className="mt-2 flex items-center justify-between gap-3 text-xs text-[var(--vintera-muted)]"><span>{loading ? "Checking addresses…" : notice}</span><span className="shrink-0 font-semibold">Powered by Google</span></div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="grid gap-2 text-sm font-semibold sm:col-span-2">Address line 1 *<input required disabled={disabled} autoComplete="address-line1" value={value.business_address_line_1} onChange={(event) => manual("business_address_line_1", event.target.value)} className={inputClass} /></label>
        <label className="grid gap-2 text-sm font-semibold sm:col-span-2">Address line 2<input disabled={disabled} autoComplete="address-line2" value={value.business_address_line_2} onChange={(event) => manual("business_address_line_2", event.target.value)} className={inputClass} /></label>
        <label className="grid gap-2 text-sm font-semibold">City *<input required disabled={disabled} autoComplete="address-level2" value={value.business_address_city} onChange={(event) => manual("business_address_city", event.target.value)} className={inputClass} /></label>
        <label className="grid gap-2 text-sm font-semibold">State *<input required disabled={disabled} autoComplete="address-level1" maxLength={2} value={value.business_address_state} onChange={(event) => manual("business_address_state", event.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2))} className={inputClass} /></label>
        <label className="grid gap-2 text-sm font-semibold">ZIP code *<input required disabled={disabled} autoComplete="postal-code" pattern="[0-9]{5}(-[0-9]{4})?" value={value.business_address_postal_code} onChange={(event) => manual("business_address_postal_code", event.target.value)} className={inputClass} /></label>
        <label className="grid gap-2 text-sm font-semibold">Country<input disabled value="United States" className={`${inputClass} bg-slate-100`} /></label>
      </div>
    </fieldset>
  );
}
